import type { ColumnGateFacts } from "@/components/canvas/detailsSide/tableDetails/ColumnList/columnActionGates";
import type { CommitStateViz } from "@/exports";
import type { ImportSourceType } from "@/components/canvas/leftSide/import/ImportSourceType";
import type { HistoryKind } from "@/components/canvas/openers/checkpoints";
import type { StoredDescriptor } from "@/lib/api/attachments";
import {
  PhysicalKind,
  SemanticType,
  data_type_semantic,
  type TableSourceJS,
} from "target_wasm";

/** What an action acts on, and from where. */
export interface Target {
  surface: "bar" | "pane";
  tableId?: string;
  tableName?: string;
  tableVersion?: number;
  source?: TableSourceJS;
  /** The focus or directory id: a table's item or a view. */
  itemId?: string;
  viewId?: string;
  columnId?: string;
  /** Every column in the selection. */
  columnIds?: readonly string[];
  /** From the row or the bar's column, never a lookup: view and table ids diverge. */
  columnName?: string;
  /** Every table in the selection. */
  tableIds: readonly string[];
}

// Column facts are the ⋮ menu's own gates; the host's are ANDed in.
export interface BarContext {
  target: Target;
  canEditModel: boolean;
  /** The pane's `canEditLinks`, without which the menu withholds Link column. */
  linksEditable: boolean;
  canCheckpoint: boolean;
  /** The history's own rule: this model's table, not an import or a catalog table. */
  canRelease: boolean;
  /** `canOpenModelAccess`, the door's own gate. */
  modelAccess: boolean;
  /** `importSources`: the Import tab's own sources. */
  importSources: readonly ImportSourceType[];
  /** No `addToDatasetRefusal`: the door's own answer. */
  canDataset: boolean;
  canShareTable: boolean;
  canTransfer: boolean;
  /** The directory's "Delete selected" gate: an editor, the model staying, `canTrashTables`. */
  canTrashTables: boolean;
  /** No `exportRefusal`: not sealed, values readable, the table there. */
  exportable: boolean;
  /** The table is there and its values reach this reader (D37). */
  valuesReadable: boolean;
  /** `historyOf`, the door's own rule. */
  history: HistoryKind | null;
  /** The pane's `canEditTableSchema`: Owned only. */
  schemaEditable: boolean;

  commitState: CommitStateViz;
  /** The selected view has a filter. */
  viewFiltered: boolean;
  /** The selected column has a condition in it. */
  columnInFilter: boolean;
  /** And they name the selected cell's value, every part of it (`unnamedParts`). */
  cellInFilter: boolean;
  hasAttachmentColumn: boolean;
  /** Only the unnamed ND default column and no rows. */
  tableIsEmpty: boolean;
  releaseIsSet: boolean;
  currentVersionIsRelease: boolean;
  /** A live import whose source has released past the version it pins. */
  newRelease: boolean;
  inAnyDataset: boolean;
  /** The table graph or the row graph is up. */
  inGraph: boolean;
  /** The views selected on the canvas: what Arrange, Align and Distribute move. */
  viewCount: number;

  column?: ColumnGateFacts;
  columnHasUnit: boolean;
  multiColumnExtractable: boolean;
  /** A cell is selected: a column action reaches its whole column. */
  cell: boolean;
  /** The stored files in the focused Attachment cell; none for any other cell. */
  cellFiles: readonly StoredDescriptor[];
  /** The focused cell's value as the filter takes it; null when the cell is empty, or none is. */
  cellValue: string | null;
}

/** A unit belongs to a quantity: whatever the header calls Number, Decimal included. */
export const isUnitBearingKind = (k: PhysicalKind): boolean =>
  data_type_semantic(k) === SemanticType.Number;

export const schemaOpen = (c: BarContext): boolean =>
  c.canEditModel && c.schemaEditable;

/** A column member: a column, the schema open, and the menu's gate. */
export const onColumn =
  (gate: (col: ColumnGateFacts, c: BarContext) => boolean) =>
  (c: BarContext): boolean =>
    !!c.column && schemaOpen(c) && gate(c.column, c);

/** A list's ⋮ knows what its host passes, and nothing a selection on the
 *  canvas would add. */
export const listMenuContext = (
  target: Target,
  host: Partial<
    Pick<
      BarContext,
      | "canEditModel"
      | "schemaEditable"
      | "linksEditable"
      | "columnHasUnit"
      | "column"
      | "multiColumnExtractable"
      | "valuesReadable"
    >
  >,
): BarContext => ({
  target,
  canEditModel: false,
  schemaEditable: false,
  linksEditable: false,
  canCheckpoint: false,
  canRelease: false,
  modelAccess: false,
  importSources: [],
  canDataset: false,
  canShareTable: false,
  canTransfer: false,
  canTrashTables: false,
  exportable: false,
  valuesReadable: false,
  history: null,
  commitState: "hidden",
  viewFiltered: false,
  columnInFilter: false,
  cellInFilter: false,
  hasAttachmentColumn: false,
  tableIsEmpty: false,
  releaseIsSet: false,
  currentVersionIsRelease: false,
  newRelease: false,
  inAnyDataset: false,
  inGraph: false,
  viewCount: 0,
  columnHasUnit: false,
  multiColumnExtractable: false,
  cell: false,
  cellFiles: [],
  cellValue: null,
  ...host,
});
